import React, { useState } from 'react';
import { CSSObject } from '@emotion/react';

import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { FormComponent, UiComponent } from 'components';
import { USER_MENU_URL } from 'data/menu';
import { useAppSelector } from 'stores';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { usePageOption } from 'hooks';
import { EnrollQuery } from 'queries';
import { IEnroll } from 'types';

const wrap: CSSObject = { display: 'flex', gap: '16px', minWidth: '816px' };

const column: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  minWidth: '400px',
};

const listItem: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px',
  borderBottom: '1px solid #ddd',
};

const selectedItem: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  marginBottom: '4px',
};

const drawRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '0 4px',
  padding: '8px 10px',
  borderRadius: '6px',
  background: '#fafafa',
  border: '1px solid #ececec',
};

const empty: CSSObject = {
  textAlign: 'center',
  color: '#999',
  margin: '20px 0',
};

interface Props {
  eventId: number;
  // 콤마 목록. 추가 확정 단계는 미선정자에 더해, 확정 뒤에 들어온
  // 신청 완료자(추가 모집분 / 취소 후 재신청분)까지 후보다.
  candidateStatus: string;
  total: number;
  isPending?: boolean;
  submitTitle: string;
  onSubmit: (enrollIds: number[]) => void;
}

/** 동명이인 구분용 — 휴대폰 뒤 4자리 */
const getTargetLabel = ({ user }: IEnroll) =>
  `${user?.name ?? '-'}(${user?.phone?.slice(-4) ?? '-'})`;

/** 이름(번호) 클릭 시 새 탭으로 회원 상세 */
function TargetLink({ enroll }: { enroll: IEnroll }) {
  const canViewUser = useAppSelector(
    (s) => s.auth.user?.permissions.includes('user:detail') ?? false,
  );
  if (!enroll.user?.id || !canViewUser) return <>{getTargetLabel(enroll)}</>;

  return (
    <Link
      to={`${USER_MENU_URL.회원목록}/${enroll.user.id}`}
      target="_blank"
      rel="noopener noreferrer"
      css={(theme) => ({
        textDecoration: 'underline',
        color: theme.color.primary,
      })}
    >
      {getTargetLabel(enroll)}
    </Link>
  );
}

function SelectVolunteerModal({
  eventId,
  candidateStatus,
  total,
  isPending,
  submitTitle,
  onSubmit,
}: Props) {
  const pageState = usePageOption(10);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<IEnroll[]>([]);
  const [drawCount, setDrawCount] = useState('');

  const { mutate: drawRandom, isPending: isDrawing } =
    EnrollQuery.useDrawRandom();

  const { data } = EnrollQuery.useGetAll({
    eventId,
    page: pageState.pageOption.page,
    size: pageState.pageOption.size,
    selectionStatus: candidateStatus,
    // 추첨 대상은 웹으로 직접 신청한 사람뿐이다 (어드민 수기 추가는 유형이 붙는다)
    withoutApplicationType: true,
    ...(name ? { name } : {}),
  });

  const isSelected = (enroll: IEnroll) =>
    selected.some((item) => item.id === enroll.id);

  const handleSelect = (enroll: IEnroll) => {
    if (isSelected(enroll)) return;
    setSelected([...selected, enroll]);
  };

  const handleRemove = (enroll: IEnroll) =>
    setSelected(selected.filter((item) => item.id !== enroll.id));

  const handleSearch = ({ name: keyword }: { name?: string }) => {
    setName(keyword?.trim() ?? '');
    pageState.handlePage({ ...pageState.pageOption, page: 1 });
  };

  // 서버가 후보 중에서 무작위로 뽑아 준다. 저장은 아니고 오른쪽 목록만 채운다.
  const handleDraw = () => {
    const count = Number(drawCount);
    if (!count || count < 1) {
      toast.error('뽑을 인원 수를 입력해주세요.');
      return;
    }

    drawRandom(
      { eventId, count, selectionStatus: candidateStatus },
      {
        onSuccess: ({ data: drawn }) => {
          setSelected(drawn.data);
          toast.success(
            drawn.total < count
              ? `대상자가 ${drawn.total}명뿐이라 ${drawn.total}명을 뽑았습니다.`
              : `${drawn.total}명을 뽑았습니다.`,
          );
        },
        onError: () => toast.error(DEFAULT_ERROR_MESSAGE),
      },
    );
  };

  return (
    <div css={wrap}>
      <div css={column}>
        <FormComponent.Form submit={handleSearch}>
          <FormComponent.SearchInput
            name="name"
            placeholder="참여자 이름으로 검색해주세요"
            css={{ width: '100%' }}
          />
        </FormComponent.Form>

        <div css={drawRow}>
          <FormComponent.Input
            type="number"
            min={1}
            value={drawCount}
            placeholder="인원수"
            css={{ width: '90px' }}
            onChange={(e) => setDrawCount(e.target.value)}
          />
          <UiComponent.Button
            variant="contained"
            color="primary"
            disabled={isDrawing}
            onClick={handleDraw}
          >
            랜덤 추첨
          </UiComponent.Button>
        </div>

        <ul css={{ overflowY: 'scroll', maxHeight: '290px' }}>
          {data?.data?.length ? (
            data.data.map((enroll: IEnroll) => (
              <li key={enroll.id} css={listItem}>
                <p>
                  <TargetLink enroll={enroll} />
                </p>
                <UiComponent.Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    isSelected(enroll)
                      ? handleRemove(enroll)
                      : handleSelect(enroll)
                  }
                >
                  {isSelected(enroll) ? '제거' : '선택'}
                </UiComponent.Button>
              </li>
            ))
          ) : (
            <p css={empty}>대상자가 없습니다.</p>
          )}
        </ul>

        <UiComponent.Pagination
          {...pageState}
          totalPage={data?.totalPages || 1}
        />
      </div>

      <div css={column}>
        <h3>선택된 참여자</h3>
        <div css={{ padding: '8px', overflowY: 'scroll', height: '290px' }}>
          {selected.length === 0 ? (
            <p css={empty}>선택된 참여자가 없습니다.</p>
          ) : (
            selected.map((enroll) => (
              <div key={enroll.id} css={selectedItem}>
                <p css={{ margin: 0 }}>
                  <TargetLink enroll={enroll} />
                </p>
                <UiComponent.Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleRemove(enroll)}
                >
                  제거
                </UiComponent.Button>
              </div>
            ))
          )}
        </div>

        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <p css={{ fontSize: '13px', color: '#8a8d94' }}>
            총 신청자 {total}명 중 {selected.length}명 선택
          </p>
          <UiComponent.Button
            variant="contained"
            color="primary"
            disabled={isPending || selected.length === 0}
            onClick={() => onSubmit(selected.map((enroll) => enroll.id))}
          >
            {submitTitle}
          </UiComponent.Button>
        </div>
      </div>
    </div>
  );
}

export default SelectVolunteerModal;
